import React, { useContext, useRef, useState, useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import classnames from 'classnames';
import _merge from 'lodash/merge';
import { getContext, useCombinedRefs } from 'common';
import useDidUpdateEffect from 'common/useDidUpdateEffect';
import useHover from 'common/useHover';
import { CONSTANTS } from 'common/ListKeyboardNav';
import TREE_CONSTANTS from 'components/Tree/constants';
import DRAG_TYPES from 'common/DragTypes';
import TreeDragLine from 'components/Tree/TreeDragLine';
import { TreeContext, TreeErrorMessage } from './';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
} = CONSTANTS;
const { TREE_BRANCH, TREE_BRANCH_LIST } = TREE_CONSTANTS;
const { TREEITEM } = DRAG_TYPES;

const HOVER_TIMEOUT_VALUE = 1200; // milliseconds

interface TreeBranchContextInterface {
  isOpen?: boolean;
  disabled?: boolean;
  selected?: boolean;
  error?: boolean;
  errorMessage?: React.ReactNode;
  level?: number;
}

const TreeBranchContext = React.createContext<TreeBranchContextInterface | null>({});

export interface TreeBranchProps extends React.ComponentPropsWithRef<'div'> {
  /** The unique id. Note: it is required for dragging feature. */
  id?: string;
  /** If true, display the list item as disabled. */
  disabled?: boolean;
  /** If true, display the list item as selected. */
  selected?: boolean;
  /** If true, place focus on this item. */
  focusItem?: boolean;
  /** If true, the base area will be rendered in the error state. */
  error?: boolean;
  /** If `error` property is true, the `errorMessage` will be displayed. `Typography` component can be used. */
  errorMessage?: React.ReactNode;
  /** Items in the tree, consider using `TreeNode`, `TreeBranch`. */
  children?: React.ReactNode;
  /** If true, the branch is expanded. */
  isOpen?: boolean;
  /** The number of milliseconds the dragged tree item must hover over the `TreeBranch` before
   *  calling the `dndDropIsOverCallback` callback function. Applicable when `Tree` property dnd=true.
   */
  dndDropIsOverTimeout?: number;
  /** Callback function fired when a tree item is dragged over this `TreeBranch`. Applicable when `Tree` property dnd=true.
   *  - argument: { isOver }
   *  -  boolean, if true, the TreeBranch has been hovered over for `dndDropIsOverTimeout` milliseconds
  */
  dndDropIsOverCallback?: (arg: { isOver: boolean }) => void;
  /** The branch depth in the tree. */
  level?: number;
  /** Function to render the content displayed in the tree branch item. */
  renderBranchContent?(...args: unknown[]): React.ReactNode;
  /** Function to render the contextual menu for this `TreeNode` or a node. Consider using `TreeMoreButton` in this function or node. */
  renderContextMenu?: (...args: unknown[]) => unknown | React.ReactNode;
  /** Properties applied to the TreeBranchList. */
  treeBranchListProps?: {
    style?: React.CSSProperties;
  };
  /** Properties applied to the TreeErrorContainer. */
  treeErrorContainerProps?: {};
  /** Properties applied to the TreeBranchContent. */
  treeBranchContentProps?: {};
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  onDoubleClick?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/**
 * Tree item that usually has children (branch). Children may be leaf or branch items.
 * Any other props will be provided to the wrapper element.
 */
const TreeBranch = React.forwardRef<HTMLElement, TreeBranchProps>((props: TreeBranchProps, forwardedRef) => getContext(({ RTL }) => {
  const {
    children,
    disabled,
    selected,
    focusItem,
    error,
    errorMessage,
    renderBranchContent,
    isOpen,
    level,
    renderContextMenu,
    treeBranchListProps,
    treeErrorContainerProps,
    treeBranchContentProps,
    dndDropIsOverTimeout,
    dndDropIsOverCallback,
    ...otherProps
  } = props;
  const {
    style: treeBranchListStyle,
    ...otherTreeBranchListProps
  } = treeBranchListProps;
  const [hoverRef, isHovered] = useHover();
  const [isFocused, setIsFocused] = useState(false);
  const treeContext = useContext(TreeContext);
  const isOverTimeoutHandle = useRef<any>(0);
  const innerRef = useRef(null);
  const focusRef = useRef(null);
  const focusHoverRef = useCombinedRefs(hoverRef, focusRef);

  useEffect(() => {
    if (focusItem === true) {
      focusRef && focusRef.current && focusRef.current.focus();
    }
  }, [focusItem]);

  const allowDragAndDrop = treeContext.dnd;
  const [{ isDragging }, drag] = useDrag({
    item: { draggedId: props.id, type: TREEITEM },
    collect: monitor => ({
      isDragging: !!monitor.isDragging(),
    }),
  });
  const [{ isOverCurrent, isOver }, drop] = useDrop({
    accept: TREEITEM,
    drop(item, monitor) {
      const didDrop = monitor.didDrop();
      if (didDrop) {
        return;
      }
      // Next line is added as "draggedId" doesn't exist in dnd API.
      const { draggedId } = item as unknown as { draggedId: string };
      allowDragAndDrop && treeContext.dndCallback({ draggedId, droppedOnToId: props.id });
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
      didDrop: monitor.didDrop(),
    }),
  });
  useDidUpdateEffect(() => {
    // callback to open the branch during dnd when hovered over > HOVER_TIMEOUT_VALUE milliseconds
    if (isOver) {
      isOverTimeoutHandle.current = setTimeout(() => {
        allowDragAndDrop && dndDropIsOverCallback && dndDropIsOverCallback({ isOver });
      }, dndDropIsOverTimeout || HOVER_TIMEOUT_VALUE);
    } else {
      clearTimeout(isOverTimeoutHandle.current);
      isOverTimeoutHandle.current = 0;
      allowDragAndDrop && dndDropIsOverCallback && dndDropIsOverCallback({ isOver });
    }
    return () => {
      clearTimeout(isOverTimeoutHandle.current);
    };
  }, [isOver]);
  const ref = useCombinedRefs(forwardedRef, innerRef, allowDragAndDrop && drag, allowDragAndDrop && drop);

  const handleClick = (event) => {
    const { onClick } = props;
    onClick && onClick(event);
  };
  const handleKeyDown = (event) => {
    const { onKeyDown } = props;
    event.persist(); // to enable TAB out of list
    onKeyDown && onKeyDown(event);
    // another event for Tree for Keyboard navigation of ARROW_LEFT
    !isOpen && treeContext && treeContext.arrowKeyCallback && treeContext.arrowKeyCallback(event);
  };
  const handleDoubleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { onDoubleClick } = props;
    onDoubleClick && onDoubleClick(event);
  };
  const handleFocus = (event) => {
    const { onFocus } = props;
    onFocus && onFocus(event);
    setIsFocused(true);
    event.stopPropagation();
  };
  const handleBlur = (event) => {
    const { onBlur } = props;
    onBlur && onBlur(event);
    setIsFocused(false);
  };

  const classes = classnames({
    [`${TREE_BRANCH}`]: true,
    [`${LIST_ITEM_TABABLE_ITEM}-${treeContext && treeContext.uniqueTreeId}`]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });
  const resolvedTreeBranchListStyle = _merge({}, {
    display: treeContext.truncateListText ? 'block' : undefined,
  }, treeBranchListStyle);
  const hasErrorMessage = error && errorMessage !== undefined;
  return (
    <Styled.TreeBranch
      ref={ref}
      RTL={RTL}
      $theme={treeContext.theme}
      isOpen={isOpen}
      {...otherProps}
    >
      <Styled.TreeBranchContent
        id={props.id && `${props.id}-content`}
        ref={focusHoverRef}
        isDragging={allowDragAndDrop && isDragging}
        dnd={allowDragAndDrop}
        className={classes}
        RTL={RTL}
        $theme={treeContext.theme}
        $isOver={allowDragAndDrop && isOverCurrent}
        level={level}
        error={error && (errorMessage !== undefined && (typeof errorMessage !== 'string' || errorMessage.length > 0))}
        tabIndex={-1}
        selected={selected}
        disabled={disabled}
        role="treeitem"
        aria-expanded={isOpen}
        aria-disabled={disabled}
        aria-selected={selected}
        {...treeBranchContentProps}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDoubleClick={handleDoubleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <TreeBranchContext.Provider value={{ disabled, selected, isOpen, error, errorMessage }} >
          {renderBranchContent && renderBranchContent()}
        </TreeBranchContext.Provider>
        <Styled.TreeContextMenu RTL={RTL} $theme={treeContext.theme} hide={!selected && !isFocused && !isHovered}>
          {renderContextMenu && (typeof renderContextMenu === 'function') && renderContextMenu()}
          {renderContextMenu && (typeof renderContextMenu === 'object') && renderContextMenu}
        </Styled.TreeContextMenu>
      </Styled.TreeBranchContent>
      {hasErrorMessage &&
        <Styled.TreeErrorContainer $theme={treeContext.theme} RTL={RTL} level={level + 1} error={error} {...treeErrorContainerProps}>
          {typeof errorMessage === 'string' ? <TreeErrorMessage>{errorMessage}</TreeErrorMessage> : errorMessage}
        </Styled.TreeErrorContainer>
      }
      {allowDragAndDrop && isOverCurrent && !isOver && <TreeDragLine />}
      <TreeBranchContext.Provider value={{ level: level + 1 }} >
        {isOpen &&
          <Styled.TreeBranchList style={resolvedTreeBranchListStyle} $theme={treeContext.theme} className={TREE_BRANCH_LIST} role="group" {...otherTreeBranchListProps}>
            {children}
          </Styled.TreeBranchList>
        }
      </TreeBranchContext.Provider>
    </Styled.TreeBranch>
  );
}));

TreeBranch.displayName = 'TreeBranch';

TreeBranch.defaultProps = {
  id: undefined,
  disabled: false,
  selected: false,
  focusItem: false,
  error: false,
  errorMessage: undefined,
  children: undefined,
  isOpen: false,
  dndDropIsOverTimeout: HOVER_TIMEOUT_VALUE,
  dndDropIsOverCallback: undefined,
  level: 0,
  renderBranchContent: undefined,
  renderContextMenu: undefined,
  treeBranchListProps: {},
  treeErrorContainerProps: {},
  treeBranchContentProps: {},
  onClick: undefined,
  onKeyDown: undefined,
  onDoubleClick: undefined,
  onFocus: undefined,
  onBlur: undefined,
};

export {
  TreeBranch as default,
  TreeBranchContext,
};
